import { ACCOUNT_DISPLAY_MODES, LABELS } from '@mm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Card, PageHeader, Spinner } from '../../components/ui/display';
import { Field, Input, Select } from '../../components/ui/inputs';
import { type TabItem, Tabs } from '../../components/ui/Tabs';
import { useToast } from '../../components/ui/Toast';
import { LOOKUPS_KEY, useLookups } from '../../hooks/useLookups';
import type { SettingView } from '../../types/models';
import { useAuth } from '../auth/AuthProvider';
import {
  advertisingPlatformsConfig,
  currenciesConfig,
  expenseCategoriesConfig,
  gstRatesConfig,
  revenueSourcesConfig,
} from '../masters/configs';
import { ImportPage } from '../imports/ImportPage';
import { MasterPage } from '../masters/MasterPage';
import { AuditLogPanel } from './AuditLogPanel';
import { UsersPanel } from './UsersPanel';

function GeneralSettings() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const editable = can('settings.write');
  const query = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<SettingView[]>('/settings'),
  });
  const [values, setValues] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (query.data) setValues(Object.fromEntries(query.data.map((s) => [s.key, s.value])));
  }, [query.data]);

  const save = useMutation({
    mutationFn: async () => {
      for (const s of query.data ?? []) {
        if (JSON.stringify(values[s.key]) !== JSON.stringify(s.value)) {
          await api.put(`/settings/${s.key}`, { value: values[s.key] });
        }
      }
    },
    onSuccess: () => {
      toast.success('Settings saved');
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Saving failed'),
  });

  if (!query.data) return <Spinner />;
  const set = (key: string, value: unknown) => setValues((v) => ({ ...v, [key]: value }));

  return (
    <Card title="General" description="Business timezone is fixed to Asia/Kolkata (IST).">
      <div className="grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Company name" htmlFor="company-name" className="sm:col-span-2">
          <Input
            id="company-name"
            value={String(values['company.name'] ?? '')}
            disabled={!editable}
            onChange={(e) => set('company.name', e.target.value)}
          />
        </Field>
        <Field label="Week starts on" htmlFor="week-start">
          <Select
            id="week-start"
            value={String(values['reports.weekStartsOn'] ?? 1)}
            disabled={!editable}
            onChange={(e) => set('reports.weekStartsOn', Number(e.target.value))}
          >
            <option value="1">Monday</option>
            <option value="0">Sunday</option>
          </Select>
        </Field>
        <Field
          label="Lock the app after (minutes without activity)"
          htmlFor="idle-lock"
          help="Only for users who turned on the app lock (PIN or fingerprint / Face ID) on their account page"
        >
          <Input
            id="idle-lock"
            type="number"
            min={1}
            max={240}
            value={String(values['security.idleLockMinutes'] ?? 15)}
            disabled={!editable}
            onChange={(e) => set('security.idleLockMinutes', Number(e.target.value))}
          />
        </Field>
        <Field label="Default year for reports" htmlFor="year-mode">
          <Select
            id="year-mode"
            value={String(values['reports.defaultYearMode'] ?? 'CALENDAR')}
            disabled={!editable}
            onChange={(e) => set('reports.defaultYearMode', e.target.value)}
          >
            <option value="CALENDAR">Calendar year (Jan–Dec)</option>
            <option value="FINANCIAL">Financial year (Apr–Mar)</option>
          </Select>
        </Field>
      </div>
      {editable && (
        <div className="mt-6">
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Save settings
          </Button>
        </div>
      )}
    </Card>
  );
}

/**
 * Settings → Marketing. The default GST rate is the default row of the GST
 * rates master: new marketing entries use it, saved entries keep their rate.
 */
function MarketingSettings() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const editable = can('settings.write') && can('masters.write');
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<SettingView[]>('/settings'),
  });
  const { data: lookups } = useLookups();
  const rates = (lookups?.gstRates ?? []).filter((r) => r.status === 'ACTIVE');
  const currentDefault = rates.find((r) => r.isDefault);
  const currentMode = String(
    settings.data?.find((x) => x.key === 'marketing.accountDisplayMode')?.value ?? 'BOTH',
  );
  const [rateId, setRateId] = useState('');
  const [mode, setMode] = useState('');

  useEffect(() => setRateId(currentDefault?.id ?? ''), [currentDefault?.id]);
  useEffect(() => setMode(currentMode), [currentMode]);

  const save = useMutation({
    mutationFn: async () => {
      if (rateId && rateId !== currentDefault?.id) {
        await api.put(`/gst-rates/${rateId}`, { isDefault: true });
      }
      if (mode !== currentMode) {
        await api.put('/settings/marketing.accountDisplayMode', { value: mode });
      }
    },
    onSuccess: () => {
      toast.success('Marketing settings saved');
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      void queryClient.invalidateQueries({ queryKey: LOOKUPS_KEY });
      void queryClient.invalidateQueries({ queryKey: ['resource', 'gst-rates'] });
      void queryClient.invalidateQueries({ queryKey: ['morning-entry'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Saving failed'),
  });

  if (!settings.data || !lookups) return <Spinner />;
  const dirty = rateId !== (currentDefault?.id ?? '') || mode !== currentMode;

  return (
    <Card
      title="Marketing"
      description="GST on marketing spend is calculated automatically and marked as eligible input credit."
    >
      <div className="grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Default GST rate"
          htmlFor="default-gst"
          help="Used for new entries. Entries already saved keep the rate they were saved with."
        >
          <Select
            id="default-gst"
            value={rateId}
            disabled={!editable}
            onChange={(e) => setRateId(e.target.value)}
          >
            {rates.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Show advertising accounts by"
          htmlFor="display-mode"
          help="Morning entry, dashboards and account lists"
        >
          <Select
            id="display-mode"
            value={mode}
            disabled={!editable}
            onChange={(e) => setMode(e.target.value)}
          >
            {ACCOUNT_DISPLAY_MODES.map((m) => (
              <option key={m} value={m}>
                {LABELS.accountDisplayMode[m]}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {editable && (
        <div className="mt-6">
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty}>
            Save marketing settings
          </Button>
        </div>
      )}
    </Card>
  );
}

export function SettingsPage() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();

  const tabs: (TabItem & { show: boolean; render: () => ReactNode })[] = [
    { id: 'general', label: 'General', show: true, render: () => <GeneralSettings /> },
    { id: 'marketing', label: 'Marketing', show: true, render: () => <MarketingSettings /> },
    { id: 'users', label: 'Users', show: can('users.manage'), render: () => <UsersPanel /> },
    {
      id: 'platforms',
      label: 'Ad platforms',
      show: true,
      render: () => <MasterPage config={advertisingPlatformsConfig} embedded />,
    },
    {
      id: 'revenue-sources',
      label: 'Revenue sources',
      show: true,
      render: () => <MasterPage config={revenueSourcesConfig} embedded />,
    },
    {
      id: 'categories',
      label: 'Expense categories',
      show: true,
      render: () => <MasterPage config={expenseCategoriesConfig} embedded />,
    },
    {
      id: 'gst-rates',
      label: 'GST rates',
      show: true,
      render: () => <MasterPage config={gstRatesConfig} embedded />,
    },
    {
      id: 'currencies',
      label: 'Currencies',
      show: true,
      render: () => <MasterPage config={currenciesConfig} embedded />,
    },
    {
      id: 'import',
      label: 'Import data',
      show: can('transactions.write'),
      render: () => <ImportPage embedded />,
    },
    { id: 'audit', label: 'Audit log', show: can('audit.read'), render: () => <AuditLogPanel /> },
  ];
  const visible = tabs.filter((t) => t.show);
  const active = visible.find((t) => t.id === params.get('tab')) ?? visible[0]!;

  return (
    <>
      <PageHeader title="Settings" />
      <Tabs
        tabs={visible}
        active={active.id}
        onChange={(id) => setParams({ tab: id }, { replace: true })}
      />
      <div className="mt-6" key={active.id}>
        {active.render()}
      </div>
    </>
  );
}
